// frontend/src/actions/returnActions.js

import api from "../utils/axiosInstance";
import * as actionTypes from "../constants/returnConstants";
import { getApiErrorMessage } from "../utils/errorMessages";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeUpper(value = "") {
  return safeText(value).toUpperCase();
}

function normalizeApiError(error) {
  return {
    message: getApiErrorMessage(error, {
      fallback:
        "We could not update your return request right now. Please try again later.",
    }),
    code: safeText(error?.response?.data?.code || error?.errorCode || ""),
    requestId: safeText(
      error?.response?.data?.requestId || error?.requestId || "",
    ),
    retryAfterSeconds: Number(
      error?.response?.data?.retryAfterSeconds ||
        error?.retryAfterSeconds ||
        0,
    ),
    payload: error?.response?.data || null,
  };
}

export const listMyReturnRequests =
  (params = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.RETURN_LIST_REQUEST });

    try {
      const { data } = await api.get("/returns", { params });

      dispatch({
        type: actionTypes.RETURN_LIST_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.RETURN_LIST_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const getReturnEligibility =
  ({ orderNumber, reasonCategory = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.RETURN_ELIGIBILITY_REQUEST });

    try {
      const { data } = await api.get(
        `/returns/orders/${encodeURIComponent(
          normalizeUpper(orderNumber),
        )}/eligibility`,
        {
          params: reasonCategory ? { reasonCategory } : {},
        },
      );

      dispatch({
        type: actionTypes.RETURN_ELIGIBILITY_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.RETURN_ELIGIBILITY_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const createReturnRequest =
  ({ orderNumber, ...payload } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.RETURN_CREATE_REQUEST });

    try {
      const { data } = await api.post(
        `/returns/orders/${encodeURIComponent(normalizeUpper(orderNumber))}`,
        payload,
      );

      dispatch({
        type: actionTypes.RETURN_CREATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.RETURN_CREATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const getReturnRequestById =
  (returnRequestId) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.RETURN_DETAILS_REQUEST });

    try {
      const { data } = await api.get(
        `/returns/${encodeURIComponent(safeText(returnRequestId))}`,
      );

      dispatch({
        type: actionTypes.RETURN_DETAILS_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.RETURN_DETAILS_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const cancelReturnRequest =
  ({ returnRequestId, reason = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.RETURN_CANCEL_REQUEST });

    try {
      const { data } = await api.patch(
        `/returns/${encodeURIComponent(safeText(returnRequestId))}/cancel`,
        { reason },
      );

      dispatch({
        type: actionTypes.RETURN_CANCEL_SUCCESS,
        payload: data,
      });

      return data;
    } catch (primaryError) {
      try {
        const { data } = await api.post(
          `/returns/${encodeURIComponent(safeText(returnRequestId))}/cancel`,
          { reason },
        );

        dispatch({
          type: actionTypes.RETURN_CANCEL_SUCCESS,
          payload: data,
        });

        return data;
      } catch (error) {
        const payloadError = normalizeApiError(error);

        dispatch({
          type: actionTypes.RETURN_CANCEL_FAIL,
          payload: payloadError,
        });

        throw payloadError;
      }
    }
  };

export const resetReturnCurrent = () => ({
  type: actionTypes.RETURN_RESET_CURRENT,
});

export const resetReturnError = () => ({
  type: actionTypes.RETURN_RESET_ERROR,
});

export const resetReturnMeta = () => ({
  type: actionTypes.RETURN_RESET_META,
});

export const resetReturnState = () => ({
  type: actionTypes.RETURN_RESET_STATE,
});
